import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp, FaGem, FaRing, FaStar, FaCertificate } from "react-icons/fa";
import ProductItem from "../components/UI/ProductItem";
import heroImage from "../assets/all-images/bg10.jpg";
import { getApiUrl, siteConfig } from "../utils/site";
import "../styles/luxury-pages.css";
import "../styles/world-class-luxury.css";
import "../styles/luxury-product.css";

const sortProducts = (items, sortOrder) => {
  const next = [...items];

  if (sortOrder === "low") {
    next.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
  } else if (sortOrder === "high") {
    next.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
  } else if (sortOrder === "name") {
    next.sort((a, b) => `${a.name || ""}`.localeCompare(`${b.name || ""}`));
  }

  return next;
};

const normalizeProduct = (product) => ({
  ...product,
  image_url: product.image_url || product.image || null,
});

const getProductsFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getCategoryIcon = (category) => {
  switch(category?.toLowerCase()) {
    case 'rings':
    case 'ring':
      return <FaRing />;
    case 'necklaces':
    case 'necklace':
      return <FaGem />;
    case 'earrings':
    case 'earring':
      return <FaStar />;
    case 'bracelets':
    case 'bracelet':
      return <FaCertificate />;
    default:
      return <FaGem />;
  }
};

const ProductListing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);
  const [productsToShow, setProductsToShow] = useState(12);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    setFetchError("");
    axios
      .get(getApiUrl("/api/products?limit=100"))
      .then((response) => {
        const rawProducts = getProductsFromResponse(response.data);
        const allProducts = rawProducts.map(normalizeProduct);

        setProducts(allProducts);

        const foundCategories = Array.from(
          new Set(allProducts.map((p) => p.category).filter(Boolean))
        );

        setCategories(["All", ...foundCategories]);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setProducts([]);
        setCategories(["All"]);
        setFetchError(
          error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Unable to load products."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredProducts = useMemo(() => {
    const matches = products.filter((product) => {
      const matchesSearch = [product.name, product.category, product.material, product.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "All" || product.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    return sortProducts(matches, sortOrder);
  }, [products, searchTerm, selectedCategory, sortOrder]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, productsToShow),
    [filteredProducts, productsToShow]
  );

  return (
    <>
      <Helmet>
        <title>Shop Gemstone Jewellery | Roha Jewellery Collection</title>
        <meta
          name="description"
          content="Browse Roha Jewellery collections with improved luxury filtering, clearer product discovery, and stronger SEO around Ethiopian gemstone and fine jewellery categories."
        />
        <link rel="canonical" href={`${siteConfig.url}/products`} />
      </Helmet>

      <main className="luxury-page">
        {/* Hero Section */}
        <section className="luxury-page__hero">
          <div className="luxury-page__hero-bg">
            <img src={heroImage} alt="Roha Jewellery Collection Background" />
          </div>
          <div className="luxury-page__hero-overlay"></div>
          <div className="luxury-page__hero-gradient"></div>
          
          <Container>
            <div className="luxury-page__hero-content">
              <div className="luxury-page__hero-eyebrow">
                <FaGem /> Jewellery Collection
              </div>
              
              <h1 className="luxury-page__hero-title">
                Shop Ethiopian <span className="luxury-page__hero-title-gradient">Gemstone Jewellery</span> with Confidence
              </h1>
              
              <p className="luxury-page__hero-subtitle">
                Search, browse, and compare opal, emerald, ruby, silver, and gold pieces through our luxury collection. 
                Each piece is handcrafted with Ethiopian gemstones and premium materials.
              </p>
              
              <div className="luxury-page__hero-actions">
                <a href="#collection" className="wc-button wc-button-primary wc-button-lg">
                  Explore Collection
                </a>
                <Link to="/design-request" className="wc-button wc-button-secondary">
                  Request Custom Jewellery
                </Link>
              </div>
              
              <div className="luxury-page__hero-tags">
                <span className="luxury-page__hero-tag">Ethiopian Opal</span>
                <span className="luxury-page__hero-tag">Gold & Silver</span>
                <span className="luxury-page__hero-tag">Handcrafted</span>
                <span className="luxury-page__hero-tag">Gemstone Quality</span>
              </div>
            </div>
          </Container>
        </section>

        {/* Collection Section */}
        <section className="luxury-section luxury-section--soft" id="collection">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaFilter /> Refined Browsing
              </div>
              <h2 className="luxury-section__title">Find the Right Piece Faster</h2>
              <p className="luxury-section__subtitle">
                Use our advanced filters to discover the perfect Ethiopian gemstone jewellery for your collection.
              </p>
            </div>
            
            {/* Filters Section */}
            <div className="luxury-product-filters">
              <div className="luxury-product-filters__header">
                <h3 className="luxury-product-filters__title">
                  <FaSearch className="me-2" /> Search & Filter
                </h3>
                <div className="text-gold fw-bold">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'Result' : 'Results'}
                </div>
              </div>
              
              <div className="luxury-product-filters__grid">
                <div className="luxury-product-filter">
                  <label className="luxury-product-filter__label">Search Products</label>
                  <input
                    type="search"
                    className="luxury-product-filter__input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search rings, opal, emerald, gold, silver..."
                  />
                </div>
                
                <div className="luxury-product-filter">
                  <label className="luxury-product-filter__label">Sort By</label>
                  <select 
                    className="luxury-product-filter__select"
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="">Recommended</option>
                    <option value="low">Price: Low to High</option>
                    <option value="high">Price: High to Low</option>
                    <option value="name">Alphabetical</option>
                  </select>
                </div>
                
                <div className="luxury-product-filter">
                  <label className="luxury-product-filter__label">Category</label>
                  <select 
                    className="luxury-product-filter__select"
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Category Pills */}
              <div className="d-flex flex-wrap gap-2 mt-4">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`luxury-page__hero-tag ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      background: selectedCategory === cat ? 'rgba(214,187,118,0.2)' : '',
                      borderColor: selectedCategory === cat ? 'rgba(214,187,118,0.4)' : ''
                    }}
                  >
                    {getCategoryIcon(cat)} {cat}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Products Grid */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-gold" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-secondary">Loading jewellery collection...</p>
              </div>
            ) : fetchError ? (
              <div className="text-center py-5">
                <FaSearch size={64} className="text-muted mb-4" />
                <h4 className="text-secondary mb-3">Unable to load products</h4>
                <p className="text-muted">{fetchError}</p>
                <p className="text-muted small mb-0">
                  Make sure the backend API is running on {getApiUrl("/api/products")}.
                </p>
              </div>
            ) : visibleProducts.length ? (
              <>
                <div className="luxury-product-grid">
                  {visibleProducts.map((product) => (
                    <ProductItem key={product.uuid || product.id} item={product} />
                  ))}
                </div>
                
                {productsToShow < filteredProducts.length && (
                  <div className="luxury-product-load-more">
                    <button
                      className="wc-button wc-button-primary"
                      onClick={() => setProductsToShow((prev) => prev + 12)}
                    >
                      View More Jewellery
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <FaSearch size={64} className="text-muted mb-4" />
                <h4 className="text-secondary mb-3">No products match your search</h4>
                <p className="text-muted">Try adjusting your filters or search terms to find what you're looking for.</p>
                <button
                  className="wc-button wc-button-secondary mt-3"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('All');
                    setSortOrder('');
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </Container>
        </section>

        {/* Features Section */}
        <section className="luxury-section">
          <Container>
            <div className="luxury-section__heading">
              <h2 className="luxury-section__title">Why Choose Roha Jewellery</h2>
              <p className="luxury-section__subtitle">
                Experience the difference of authentic Ethiopian gemstone jewellery crafted with luxury and precision.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--3">
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Better Collection Hierarchy</a>
                  </h3>
                  <p className="luxury-card__description">
                    Cleaner layout and intuitive navigation help customers discover the perfect piece faster with enhanced visual organization.
                  </p>
                </div>
              </div>
              
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Keyword-Rich Context</a>
                  </h3>
                  <p className="luxury-card__description">
                    Improved SEO optimization for gemstone discovery with detailed product descriptions and category-specific metadata.
                  </p>
                </div>
              </div>
              
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Stronger Conversion Flow</a>
                  </h3>
                  <p className="luxury-card__description">
                    Smoother path from browsing to purchase with clear calls-to-action, detailed product information, and trust signals.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-5">
              <Link to="/design-request" className="wc-button wc-button-secondary">
                Need a Bespoke Design Instead?
              </Link>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="luxury-section luxury-section--dark">
          <Container>
            <div className="luxury-section__heading">
              <h2 className="luxury-section__title">Ready to Find Your Perfect Piece?</h2>
              <p className="luxury-section__subtitle">
                Can't find exactly what you're looking for? Our custom design service creates unique jewellery tailored to your vision.
              </p>
            </div>
            
            <div className="text-center">
              <Link to="/design-request" className="wc-button wc-button-primary wc-button-lg me-3">
                Start Custom Design
              </Link>
              <Link to="/contact" className="wc-button wc-button-secondary">
                Contact Our Experts
              </Link>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default ProductListing;
